"use client";

import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getAuth, connectAuthEmulator, signInWithCustomToken, signOut } from "firebase/auth";
import { isSupported as analyticsIsSupported, getAnalytics } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// Populated from NEXT_PUBLIC_* env vars once a real Firebase project exists -- see
// docs/requirements/risks-assumptions-blockers.md and README.md for the "not yet deployed,
// needs a Firebase project + Blaze billing" blocker. Safe to import with no config present;
// it will only actually fail when something tries to talk to Firestore/Functions.
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Client-side half of App Check -- attaches a token to every Firestore/Functions/Auth request
// proving it came from this real web app, not a scraped API key replayed from a script. This
// alone does nothing until the matching server-side half is turned on for each product in the
// Firebase console (Build -> App Check -> Apps -> register this site key, then Enforce per
// product) -- that console step still needs a human, this only prepares the client to pass.
//
// Was broken and briefly disabled (2026-09-01): the old site key here didn't correspond to any
// real reCAPTCHA Enterprise key in this project, so the exchange always failed -- and once the
// reCAPTCHA Enterprise API itself was enabled, that failure became a real network round trip the
// Functions SDK waited on before every callable call, intermittently breaking /book and every
// other form ("Could not load availability", etc). Fixed by creating a real key (`gcloud
// recaptcha keys create`, scoped to hoopyogi.com/*.web.app/*.firebaseapp.com/localhost, SCORE
// integration) and pointing both this env var and App Check's own recaptchaEnterpriseConfig at
// it. Enforcement itself is still UNENFORCED for every product at the project level -- this only
// makes token issuance work again, it doesn't turn on any actual blocking.
if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(process.env.NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

export const db = getFirestore(firebaseApp);
export const functions = getFunctions(firebaseApp, "asia-south1");

// Lazy, unlike db/functions above -- calling getAuth() is what triggers Firebase Auth's
// cross-domain persistence iframe (a real network round trip to <project>.firebaseapp.com,
// ~1.3s on a throttled connection per Lighthouse). Every page pulls in this module transitively
// (useSiteNavigation/useSiteBlocks, both used sitewide, import db/functions from here), but only
// admin and account pages actually touch auth -- eagerly creating it on every page load was
// paying that cost on the homepage for no reason. getAuth() itself is idempotent/cached by the
// SDK per app instance, so this is just deferring the same singleton, not creating a new one.
let _auth: ReturnType<typeof getAuth> | undefined;
export function getAuthInstance() {
  if (!_auth) _auth = getAuth(firebaseApp);
  return _auth;
}

// This is the same GA4 property Firebase's own console Analytics tab reads from -- a second,
// separate signal alongside GTM (see layout.tsx's NEXT_PUBLIC_GTM_ID), not a replacement for
// it. isSupported() rules out SSR and browsers without IndexedDB/cookie support (Analytics
// needs both); ConsentDefaults' Consent Mode denial (set beforeInteractive, before this module
// even runs) applies here too, since the Analytics SDK reads/writes the same window.dataLayer.
if (typeof window !== "undefined" && firebaseConfig.measurementId) {
  void analyticsIsSupported().then((supported) => {
    if (supported) getAnalytics(firebaseApp);
  });
}

// Local-only: point the client SDKs at `firebase emulators:start` instead of the real project.
// Opt-in via NEXT_PUBLIC_USE_EMULATORS=true in .env.local -- never set in a deployed environment.
if (process.env.NEXT_PUBLIC_USE_EMULATORS === "true" && typeof window !== "undefined") {
  const g = globalThis as unknown as { __hyEmulatorsConnected?: boolean };
  if (!g.__hyEmulatorsConnected) {
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
    connectAuthEmulator(getAuthInstance(), "http://127.0.0.1:9099");
    g.__hyEmulatorsConnected = true;
  }
}

/**
 * Exchanges the custom token verifyPhoneOtp returns for a real signed-in session -- the whole
 * account mechanism for customers now (booking, batch enrollment, /account), replacing the
 * earlier anonymous-session-plus-optional-email/password-account split entirely. The uid this
 * produces is the phone number itself (see functions/src/otp.ts), the same value whether this
 * is a first-time or returning customer, so "sign up" and "sign in" are the same action.
 */
export async function signInWithPhoneCustomToken(customToken: string) {
  return signInWithCustomToken(getAuthInstance(), customToken);
}

// Used for both admin and customer sign-out -- a thin wrapper, not tied to either.
export async function signOutCustomer() {
  return signOut(getAuthInstance());
}
